import { useCallback, useRef } from 'react';

import {
	EDITOR_ROLES,
	type EditableDescriptor,
	type EditorRole,
	type LabelDiagnostics,
} from './types.ts';

type ScanResult = {
	descriptors: EditableDescriptor[];
	diagnostics: LabelDiagnostics;
};

/**
 * Algorithm B: Scan iframe DOM for bw-* labeled elements, normalize duplicates,
 * serialize, and compute diagnostics.
 */
export function useEditableElements(
	onHtmlChange?: (html: string) => void,
	onDiagnosticsChange?: (diagnostics: LabelDiagnostics) => void,
) {
	const lastEmittedHtmlRef = useRef<string>('');

	const resetEmittedHtml = useCallback(() => {
		lastEmittedHtmlRef.current = '';
	}, []);

	const scan = useCallback(
		(iframeDoc: Document): ScanResult => {
			const elements = Array.from(
				iframeDoc.querySelectorAll<HTMLElement>('[id^="bw-"]'),
			);

			let skippedMalformedLabels = 0;
			let duplicateIdsDetected = 0;
			const descriptors: EditableDescriptor[] = [];
			const idCounts = new Map<string, number>();

			// First pass: parse and validate
			const parsed: {
				element: HTMLElement;
				id: string;
				role: EditorRole;
				uid: string;
			}[] = [];
			for (const el of elements) {
				const id = el.id;
				const parts = id.split('-');
				// bw-<role>-<uid...>
				if (parts.length < 3 || parts[0] !== 'bw') {
					skippedMalformedLabels++;
					continue;
				}
				const role = parts[1] as EditorRole;
				if (!EDITOR_ROLES.has(role)) {
					skippedMalformedLabels++;
					continue;
				}
				const uid = parts.slice(2).join('-');
				if (!uid) {
					skippedMalformedLabels++;
					continue;
				}
				parsed.push({ element: el, id, role, uid });
			}

			// Second pass: detect and resolve duplicates
			for (const item of parsed) {
				const count = (idCounts.get(item.id) ?? 0) + 1;
				idCounts.set(item.id, count);

				if (count > 1) {
					// Rename duplicate
					duplicateIdsDetected++;
					const newId = `${item.id}-d${count}`;
					item.element.id = newId;
					const newParts = newId.split('-');
					const newUid = newParts.slice(2).join('-');
					descriptors.push({
						id: newId,
						role: item.role,
						uid: newUid,
					});
				} else {
					descriptors.push({
						id: item.id,
						role: item.role,
						uid: item.uid,
					});
				}
			}

			const diagnostics: LabelDiagnostics = {
				labeledCount: descriptors.length,
				skippedMalformedLabels,
				duplicateIdsDetected,
			};

			// Serialize normalized HTML immediately
			const serialized = iframeDoc.documentElement.outerHTML;
			if (serialized !== lastEmittedHtmlRef.current) {
				lastEmittedHtmlRef.current = serialized;
				onHtmlChange?.(serialized);
			}

			if (descriptors.length === 0) {
				console.warn(
					'[InlineEmailEditor] scan found 0 editable elements.',
					`Total bw-* elements: ${elements.length}`,
					`Skipped: ${skippedMalformedLabels}`,
				);
			}

			onDiagnosticsChange?.(diagnostics);

			return { descriptors, diagnostics };
		},
		[onHtmlChange, onDiagnosticsChange],
	);

	return { scan, resetEmittedHtml, lastEmittedHtmlRef };
}
